import { Component, OnInit } from "@angular/core";
import { StatsService } from "@/_services/stats.service";
import { debounceTime, Observable } from "rxjs";
import { shareReplay, tap, startWith, map } from "rxjs/operators";
import {
    buildPastDaysDateRange,
    reapplyTimeFrameRange,
} from "@/_utility/date.utils";
import { StatsPromotionResult } from "@/_models/stats-promotion.interface";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { FilterService } from "@/_services/filter.service";
import { LOCAL_STORAGE_KEYS } from "@/_models/filters/local-storage-keys";
import { RadioOption } from "@/components/shared/radio-list/radio-list.component";
import { timeFrameListValues } from "@/components/safety-assurance/issue-list/issue-list.component";
import { GroupsService } from "@/_services/groups.service";
import {
    GroupFilterOptions,
    toStatsGroupParams,
    withoutGroupFilters,
} from "@/_models/filters/group-filter-options";
import { isEnabled } from "@features/functions";
import { UserService } from "@/_services/user.service";

@UntilDestroy()
@Component({
    selector: "app-performance-promotion",
    templateUrl: "./performance-promotion.component.html",
    styleUrls: ["./performance-promotion.component.scss"],
})
export class PerformancePromotionComponent implements OnInit {
    stats$: Observable<StatsPromotionResult>;
    filterForm: UntypedFormGroup;
    timeFrameList: RadioOption[] = timeFrameListValues;
    groupsEnabled = isEnabled("groupsLocations");
    groupFilterOptions$: Observable<GroupFilterOptions>;
    userList$: Observable<RadioOption[]>;
    currentFilters;

    constructor(
        private statsService: StatsService,
        private filterService: FilterService,
        private formBuilder: UntypedFormBuilder,
        private groupsService: GroupsService,
        private userService: UserService,
    ) {}

    ngOnInit(): void {
        this.buildFilterForm();

        this.userList$ = this.userService.getUsers().pipe(
            map((users) =>
                users.map((user) => ({
                    label: user.fullName,
                    value: user._id,
                })),
            ),
            shareReplay(),
        );

        if (this.groupsEnabled) {
            this.groupFilterOptions$ = this.groupsService
                .getGroupFilterOptions()
                .pipe(shareReplay());
        }
    }

    buildFilterForm() {
        const [defaultStart, defaultEnd] = buildPastDaysDateRange(-30);
        const initialFilters = {
            timeFrame: -30,
            createdAt: [defaultStart, defaultEnd],
            individual: "",
        };

        const savedFilters = this.filterService.loadSimpleFilters(
            LOCAL_STORAGE_KEYS.PROMOTION_FILTERS,
        );

        const filters = savedFilters
            ? { ...initialFilters, ...savedFilters }
            : initialFilters;

        this.filterForm = this.formBuilder.group({
            timeFrame: [filters.timeFrame],
            createdAt: [filters.createdAt],
            individual: [filters.individual],
        });

        if (this.groupsEnabled) {
            this.filterForm.addControl("groups", this.formBuilder.control([]));
        }

        reapplyTimeFrameRange(this.filterForm, "createdAt");

        this.filterForm.valueChanges
            .pipe(
                startWith(this.filterForm.value),
                debounceTime(500),
                untilDestroyed(this),
                tap((changes) => {
                    this.currentFilters = { ...changes };
                    this.filterService.saveSimpleFilters(
                        LOCAL_STORAGE_KEYS.PROMOTION_FILTERS,
                        withoutGroupFilters({ ...changes }),
                    );
                }),
                tap(() => {
                    this.updateStatsData();
                }),
            )
            .subscribe();
    }

    updateStatsData() {
        this.stats$ = this.statsService
            .getPromotionStats(toStatsGroupParams(this.filterForm.value))
            .pipe(shareReplay());
    }
}
